import * as React from 'react'

/**
 * matchMedia hook (old MUI useMediaQuery). Pass a plain media query string,
 * e.g. useMediaQuery('(max-width: 900px)').
 */
export function useMediaQuery(query: string): boolean {
  const cleaned = query.replace(/^@media\s*/, '')
  const [matches, setMatches] = React.useState(() =>
    typeof window === 'undefined' ? false : window.matchMedia(cleaned).matches,
  )

  React.useEffect(() => {
    const mql = window.matchMedia(cleaned)
    setMatches(mql.matches)
    const listener = (e: MediaQueryListEvent) => setMatches(e.matches)
    mql.addEventListener('change', listener)
    return () => mql.removeEventListener('change', listener)
  }, [cleaned])

  return matches
}

/** Old MUI breakpoint helpers, for converted useMediaQuery call sites. */
export const breakpoints = {
  down: (key: 'sm' | 'md' | 'lg' | 'xl') =>
    `(max-width: ${{ sm: 599.95, md: 899.95, lg: 1199.95, xl: 1535.95 }[key]}px)`,
  up: (key: 'sm' | 'md' | 'lg' | 'xl') =>
    `(min-width: ${{ sm: 600, md: 900, lg: 1200, xl: 1536 }[key]}px)`,
}

export interface ClickAwayListenerProps {
  onClickAway: (event: MouseEvent | TouchEvent) => void
  children: React.ReactElement
}

/** Calls onClickAway when a pointer event lands outside the child. */
export function ClickAwayListener({
  onClickAway,
  children,
}: ClickAwayListenerProps) {
  const nodeRef = React.useRef<HTMLElement | null>(null)
  const handlerRef = React.useRef(onClickAway)
  handlerRef.current = onClickAway

  React.useEffect(() => {
    const listener = (event: MouseEvent | TouchEvent) => {
      if (
        nodeRef.current &&
        event.target instanceof Node &&
        !nodeRef.current.contains(event.target)
      ) {
        handlerRef.current(event)
      }
    }
    document.addEventListener('mousedown', listener)
    document.addEventListener('touchstart', listener)
    return () => {
      document.removeEventListener('mousedown', listener)
      document.removeEventListener('touchstart', listener)
    }
  }, [])

  return (
    <span ref={nodeRef as React.Ref<HTMLSpanElement>} className="contents">
      {children}
    </span>
  )
}

/** Simple show/hide with a height transition (old MUI Collapse). */
export function Collapse({
  in: open,
  children,
  className,
  timeout,
}: {
  in?: boolean
  children?: React.ReactNode
  className?: string
  timeout?: number | string
}) {
  return (
    <div
      className={className}
      style={{
        display: 'grid',
        gridTemplateRows: open ? '1fr' : '0fr',
        transition: `grid-template-rows ${
          typeof timeout === 'number' ? `${timeout}ms` : '225ms'
        } ease`,
      }}
      aria-hidden={!open}
    >
      <div className="min-h-0 overflow-hidden">{children}</div>
    </div>
  )
}

/** Fade-in wrapper (old MUI Fade/Grow/Zoom); renders nothing when closed. */
export function Fade({
  in: open,
  children,
  className,
}: {
  in?: boolean
  children?: React.ReactNode
  className?: string
  timeout?: number | { enter?: number; exit?: number }
}) {
  if (!open) return null
  return (
    <div className={cnJoin('animate-ps-fade-in', className)}>{children}</div>
  )
}

export const Grow = Fade
export const Zoom = Fade

function cnJoin(...parts: (string | undefined)[]) {
  return parts.filter(Boolean).join(' ')
}
